import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { ErrorModal } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
	AgGridEvent,
	ColDef,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { isNil } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Stand } from "../models/Stand";
import { getStandsByPart } from "../repositories/StandRepository";

interface Props {
	standsOfPart: Stand[];
	onSelectionChanged: Function;
}

type useStandsByPartProps = {
	partId?: number;
	onError?: (error: Error) => void;
	onSuccess?: (data: Stand[]) => void;
};

export const useStandsByPart = ({
	partId,
	onError,
	onSuccess,
}: useStandsByPartProps) => {
	return useQuery<Stand[], Error>({
		queryKey: ["standsByPart", partId],
		queryFn: ({ signal }) =>
			getStandsByPart({ partId: partId ?? null }, signal),
		onError: onError,
		onSuccess: onSuccess,
	});
};

export const StandsOfPartSelector = ({
	standsOfPart,
	onSelectionChanged,
}: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [allStands, setAllStands] = useState<Stand[]>([]);
	const [error, setError] = useState<string>("");

	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const { data: stands, isLoading } = useStandsByPart({
		onError: (error) => setError(error.message),
		onSuccess: (data) => {
			if (data && data.length > 0) {
				setAllStands([...data]);
			}
		},
	});

	const handlerFilter = (e: any) => {
		setFilterInput(e.target.value);
	};

	const sortRows = (event: AgGridEvent) => {
		event.api.applyColumnState({
			state: [{ colId: "StandName", sort: "asc", sortIndex: 0 }],
		});
	};

	const rows = useMemo(() => {
		return allStands.map(({ StandId, StandName }) => {
			return {
				StandId: StandId,
				StandName: StandName,
			};
		});
	}, [allStands]);

	const columns: ColDef[] = [
		{
			field: "",
			flex: 1,
			maxWidth: 100,
			checkboxSelection: true,
			showDisabledCheckboxes: true,
			sortIndex: 1,
			sortable: false,
		},
		{
			field: "StandName",
			headerName: "Stand",
			flex: 2,
			sortIndex: 0,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.StandName?.localeCompare(nodeB.data.StandName);
				}
			},
		},
	];

	const { defaultColDef } = useGridDefinitions({});

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows(event);
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			params.api.forEachNode((node) => {
				node.setSelected(
					!!node.data &&
						!isNil(standsOfPart.find((p) => p.StandId === node.data.StandId)),
				);
			});
			sortRows(params);
		},
		[sortRows],
	);

	const onGridReady = useCallback(
		(event: GridReadyEvent) => {
			setGridApi(event.api);
		},
		[setGridApi],
	);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	return (
		<>
			<Grid2
				container
				size={{ xs: 12, md: 12 }}
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextField
						id="outlined-basic"
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						style={{ width: "100%" }}
					/>
				</Grid2>
			</Grid2>
			<div style={{ width: "100%", height: "40vh" }}>
				<AgGridReact
					loading={isLoading}
					getRowId={(params) => params.data.StandId}
					onGridReady={onGridReady}
					rowHeight={25}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={defaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					animateRows
					onSelectionChanged={onAgGridSelectionChanged}
					gridOptions={{
						rowSelection: {
							mode: "multiRow",
							checkboxes: false,
							enableSelectionWithoutKeys: true,
							enableClickSelection: true,
						},
					}}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
